import { Injectable, SecurityContext, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { game } from '../../game';
import { ItemDataService } from './item-data.service';

interface BundledRuleContent {
  id: string;
  content: string;
}

type ExplainerMap = Record<string, string>;

@Injectable({
  providedIn: 'root',
})
export class ExplainerService {
  private readonly ruleExplainers = new Map<string, SafeHtml>();
  private readonly uniqueExplainers = new Map<string, SafeHtml>();
  private readonly recipeExplainers = new Map<string, SafeHtml>();
  private readonly equipmentExplainerIds = new Map<string, string[]>();

  readonly loaded = signal(false);
  private preload$?: Observable<boolean>;

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private itemDataService: ItemDataService,
  ) {
    this.buildEquipmentExplainerIndex();
  }

  initializePreloading(): Observable<boolean> {
    if (!this.preload$) {
      this.preload$ = forkJoin({
        rules: this.http.get<BundledRuleContent[]>(`/data/${game}/rules-content.json`).pipe(catchError(() => of([]))),
        uniqueExplainers: this.http.get<ExplainerMap>(`/data/${game}/unique-explainers.json`).pipe(catchError(() => of({}))),
        recipeExplainers: this.http.get<ExplainerMap>(`/data/${game}/recipe-explainers.json`).pipe(catchError(() => of({}))),
      }).pipe(
        tap(({ rules, uniqueExplainers, recipeExplainers }) => {
          this.loadRuleExplainers(rules);
          this.loadJsonExplainers(this.uniqueExplainers, uniqueExplainers);
          this.loadJsonExplainers(this.recipeExplainers, recipeExplainers);
          this.loaded.set(true);
        }),
        map(() => true),
        catchError(() => {
          this.loaded.set(false);
          return of(false);
        }),
        shareReplay(1),
      );
    }

    return this.preload$;
  }

  isLoaded(): boolean {
    return this.loaded();
  }

  getRuleExplainers(completeId: string | undefined): SafeHtml[] {
    if (!completeId || !this.isLoaded()) {
      return [];
    }

    const seen = new Set<string>();
    const results: SafeHtml[] = [];

    for (const ruleId of this.iterRuleIdCandidates(completeId)) {
      this.collectRuleExplainers(ruleId, seen, results);
    }

    return results;
  }

  getRuleExplainersForRuleId(ruleId: string | undefined): SafeHtml[] {
    if (!ruleId || !this.isLoaded()) {
      return [];
    }

    const seen = new Set<string>();
    const results: SafeHtml[] = [];
    this.collectRuleExplainers(ruleId, seen, results);
    return results;
  }

  getCombinedRuleExplainerHtml(ruleIds: string[]): SafeHtml | null {
    const explainers = ruleIds.flatMap(ruleId => this.getRuleExplainersForRuleId(ruleId));
    if (explainers.length === 0) {
      return null;
    }

    const combined = explainers
      .map(explainer => this.sanitizer.sanitize(SecurityContext.HTML, explainer) ?? '')
      .filter(Boolean)
      .join('<br /><br />');

    return combined ? this.sanitizer.bypassSecurityTrustHtml(combined) : null;
  }

  getUniqueExplainer(uniqueName: string | undefined): SafeHtml | null {
    if (!uniqueName || !this.isLoaded()) {
      return null;
    }
    return this.uniqueExplainers.get(uniqueName) ?? null;
  }

  getRecipeExplainer(nameOrBaseType: string | undefined): SafeHtml | null {
    if (!nameOrBaseType || !this.isLoaded()) {
      return null;
    }
    const key = toSnakeCase(nameOrBaseType);
    return this.recipeExplainers.get(key) ?? null;
  }

  getRecipeExplainerForItem(uniqueName?: string, baseType?: string): SafeHtml | null {
    if (uniqueName) {
      const byUnique = this.getRecipeExplainer(uniqueName);
      if (byUnique) return byUnique;
    }
    if (baseType) {
      return this.getRecipeExplainer(baseType);
    }
    return null;
  }

  static toSnakeCase(name: string): string {
    return toSnakeCase(name);
  }

  private buildEquipmentExplainerIndex(): void {
    for (const equipment of this.itemDataService.getEquipmentData()) {
      if (equipment.explainerIds?.length) {
        this.equipmentExplainerIds.set(equipment.ruleId, equipment.explainerIds);
      }
    }
  }

  private collectRuleExplainers(ruleId: string, seen: Set<string>, results: SafeHtml[]): void {
    const addById = (id: string) => {
      if (seen.has(id)) return;
      const content = this.ruleExplainers.get(id);
      if (content) {
        seen.add(id);
        results.push(content);
      }
    };

    addById(ruleId);

    let longestPrefixMatch = '';
    for (const id of this.ruleExplainers.keys()) {
      if (ruleId !== id && ruleId.startsWith(`${id}-`) && id.length > longestPrefixMatch.length) {
        longestPrefixMatch = id;
      }
    }
    if (longestPrefixMatch) {
      addById(longestPrefixMatch);
    }

    const explainerIds = this.equipmentExplainerIds.get(ruleId);
    if (explainerIds) {
      for (const explainerId of explainerIds) {
        addById(explainerId);
      }
    }
  }

  private loadRuleExplainers(rules: BundledRuleContent[]): void {
    this.ruleExplainers.clear();
    for (const rule of rules) {
      this.ruleExplainers.set(rule.id, this.sanitizeHtml(rule.content));
    }
  }

  private loadJsonExplainers(target: Map<string, SafeHtml>, source: ExplainerMap): void {
    target.clear();
    for (const [key, content] of Object.entries(source)) {
      target.set(key, this.sanitizeHtml(content));
    }
  }

  private sanitizeHtml(html: string): SafeHtml {
    const clean = this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }

  private *iterRuleIdCandidates(completeId: string): Generator<string> {
    let current = completeId;
    while (current) {
      yield current;
      const slashIndex = current.lastIndexOf('/');
      if (slashIndex === -1) break;
      current = current.slice(0, slashIndex);
    }
  }
}

export function toSnakeCase(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}
